import { Button } from "@src/lib/ui/Button";
import { Separator } from "@src/lib/ui/Separator";
import Text from "@src/lib/ui/Text";
import XIcon from "@src/lib/icons/18px/x";
import { Input } from "@src/lib/ui/Input";
import { getLinkType, humanFileSize, linkTypeCopy } from "@src/lib/utils/files";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useOpenGraphDataQuery } from "@src/features/contentDetail/ui/MediaPreview.generated";
import { FileInput } from "./BulkUploadTrainingResourceDialog";
import { MediaPreview } from "./MediaPreview";
import { useTranslation } from "@src/features/contentDetail/utils/translationSets";

type Props = {
  file: FileInput;
  updateFileName: (id: string, language: Language, value: string) => void;
  updateFileDescription: (
    id: string,
    language: Language,
    value: string,
  ) => void;
  removeFile: (id: string) => void;
  language: Language;
  uploadProgress?: number;
  isUploading?: boolean;
};
export const UploadedFile: FC<Props> = ({
  file,
  updateFileDescription,
  updateFileName,
  removeFile,
  language,
  uploadProgress = 0,
  isUploading = false,
}) => {
  const [descriptionVisible, setDescriptionVisible] = useState(false);
  const type = file.url && getLinkType(file.url);
  const fileNameTranslation = useTranslation(file.name, language).text;
  const fileDescriptionTranslation = useTranslation(
    file.description,
    language,
  ).text;
  // A link has no file size; it is named after its page, and the line under
  // the name gives its domain rather than repeating the whole URL.
  const isLink = Boolean(file.url) && !file.size;
  const hostname = useMemo(() => {
    if (!isLink || !file.url) return null;
    try {
      return new URL(
        /^[a-z][a-z\d+.-]*:/i.test(file.url) ? file.url : `https://${file.url}`,
      ).hostname.replace(/^www\./, "");
    } catch {
      return null;
    }
  }, [isLink, file.url]);
  const { data: ogData } = useOpenGraphDataQuery({
    skip: !isLink || !file.url,
    variables: { url: file.url ?? "" },
  });
  const ogTitle = ogData?.OpenGraphData?.["og:title"];
  // Only replaces the placeholder name (the URL itself), once — never a name
  // someone typed.
  const namedFromPage = useRef(false);
  useEffect(() => {
    if (namedFromPage.current || typeof ogTitle !== "string") return;
    if (!ogTitle.trim() || file.name.en !== file.url) return;
    namedFromPage.current = true;
    updateFileName(file.id, "en", ogTitle.trim());
  }, [ogTitle, file.id, file.name.en, file.url, updateFileName]);
  return (
    <div className="flex w-full flex-col rounded-2xl border border-border-primary bg-bg-primary">
      <div className="flex w-full min-w-0 flex-1 flex-row items-center gap-3 self-stretch p-3">
        <MediaPreview url={file.url} isUploading={isUploading} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Text ellipsis variant="compact-title" className="text-text-primary">
            {fileNameTranslation || file.name.en}
          </Text>
          {isUploading && (
            <Text
              variant="fine-print-description"
              className="text-text-tertiary"
            >
              Uploading... {uploadProgress}%
            </Text>
          )}
          {!file.url && !isUploading && (
            <Text
              variant="fine-print-description"
              className="text-text-tertiary"
            >
              Uploading....
            </Text>
          )}
          {file.url && (
            <div className="flex min-w-0 flex-row items-center gap-2">
              <Text
                variant="fine-print-description"
                className="shrink-0 text-text-tertiary"
              >
                {type ? linkTypeCopy[type] : "File"}
              </Text>
              <span className="size-[3px] shrink-0 rounded-full bg-text-tertiary" />
              <span className="min-w-0 truncate text-xs-v4 text-text-tertiary">
                {file.size ? humanFileSize(file.size) : hostname ?? file.url}
              </span>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          className="shrink-0 self-start"
          aria-label="Remove"
          dataTestId="bulk-upload-remove-file"
          onClick={() => removeFile(file.id)}
        >
          <XIcon />
        </Button>
      </div>

      <Separator />
      <div className="flex flex-col gap-2 self-stretch p-3">
        <Input
          value={fileNameTranslation || ""}
          onChange={(e) => {
            updateFileName(file.id, language, e.target.value);
          }}
          label="Name"
        />
        {descriptionVisible && (
          <Input
            value={fileDescriptionTranslation || ""}
            onChange={(e) => {
              updateFileDescription(file.id, language, e.target.value);
            }}
            label="Description"
            autoFocus
          />
        )}
        {!descriptionVisible && (
          <Button variant="ghost" onClick={() => setDescriptionVisible(true)}>
            + Add description
          </Button>
        )}
      </div>
    </div>
  );
};
